import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { gameSightDisabled } from '@ng-icons/game-icons';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from "primeng/tooltip";
import { combineLatest, map, Observable, startWith, Subscription, tap } from 'rxjs';
import { ApiClientService } from '../../../services/apiclient/api-client.service';
import { Medicine } from '../../../services/apiclient/Medicine';
import { NotFound } from '../../../services/apiclient/not-found';
import { Rabbit } from '../../../services/apiclient/Rabbit';

@Component({
  selector: 'app-weight-entry-edit',
  imports: [
    AsyncPipe,
    ButtonModule,
    ChipModule,
    DatePickerModule,
    FloatLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    ReactiveFormsModule,
    SelectModule,
    SkeletonModule,
    TooltipModule,
    NgIconComponent
  ],
  providers: [provideIcons({
    gameSightDisabled
  })],
  templateUrl: './weight-entry-edit.component.html',
  styleUrl: './weight-entry-edit.component.css',
})
export class WeightEntryEditComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly api: ApiClientService = inject(ApiClientService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly messageService: MessageService = inject(MessageService);

  entryId: string | null = null;
  formGroup = this.formBuilder.group({
    date: [<Date | null>null],
    time: [<Date | null>null],
    rabbit: [<Rabbit | null>null, Validators.required],
    weight: [
      <number | null>null,
      Validators.min(1),
    ],
    medicines: [<Medicine[]>[]],
    comment: [<string | null>null],
  });

  private readonly isFormInvalid$ = this.formGroup.statusChanges.pipe(
    startWith(this.formGroup.status),
    map((status) => status === 'INVALID'),
  );

  // Save selected rabbit separately to reliably react to changes, including while initializing
  private readonly selectedRabbit = signal<Rabbit | null>(null);

  protected readonly isSelectedRabbitDisabled$ = toObservable(this.selectedRabbit).pipe(
    tap((value) => console.log('Selected rabbit changed:', value)),
    map((rabbit) => rabbit?.isDisabled ?? false),
  );

  protected readonly isSubmitDisabled$ = combineLatest([
    this.isFormInvalid$,
    this.isSelectedRabbitDisabled$,
  ]).pipe(
    map(([isFormInvalid, isRabbitDisabled]) => isFormInvalid || isRabbitDisabled),
  );

  getWeightEntrySubscription: Subscription | null = null;
  loaded: boolean = false;

  rabbits$: Observable<Rabbit[]> | undefined;
  medicines$: Observable<Medicine[]> | undefined;

  @Input()
  set id(entryId: string | null) {
    this.entryId = entryId;
  }

  constructor() {
    this.setSelectedRabbit();
  }

  private setSelectedRabbit() {
    this.formGroup.controls['rabbit'].valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((rabbit) => this.selectedRabbit.set(rabbit)),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.loadEntry();
    this.loadValues();
  }

  loadEntry(): void {
    if (!this.entryId) {
      this.loaded = true;
      return;
    }
    this.getWeightEntrySubscription = this.api
      .getPreparedWeightEntryById(this.entryId!)
      .subscribe(async (next) => {
        this.loaded = true;
        if (next instanceof NotFound) {
          await this.router.navigateByUrl(`/weight-entries/create`);
          return;
        }

        this.formGroup.patchValue({
          date: new Date(Date.parse(next.weighedAt)),
          time: new Date(Date.parse(next.weighedAt)),
          weight: next.weight,
          medicines: next.medicineObjects,
          comment: next.comment,
        });
        // Set rabbit explicitly to trigger valueChanges
        this.formGroup.controls['rabbit'].setValue(next.rabbit);
      });
  }

  loadValues(): void {
    this.rabbits$ = this.api.getRabbits();
    this.medicines$ = this.api
      .getMedicines()
      .pipe(
        map((medicines) =>
          medicines.sort((a, b) => a.name.localeCompare(b.name)),
        ),
      );
  }

  ngOnDestroy() {
    this.getWeightEntrySubscription?.unsubscribe();
  }

  async navigateToOverviewPage(): Promise<void> {
    await this.router.navigateByUrl(`/weight-entries`);
  }

  onSubmit(): void {
    if (!this.entryId) {
      this.createEntry();
      return;
    }

    this.updateEntry();
  }

  createEntry() {
    this.api
      .createWeightEntry(
        this.getWeighedAt(),
        this.formGroup.controls['weight'].value,
        this.formGroup.controls['comment'].value,
        this.formGroup.controls['rabbit'].value!.id,
        this.formGroup.controls['medicines'].value?.map((x) => x.id) ?? [],
      )
      .subscribe({
        next: (_) => {
          this.router.navigateByUrl('/weight-entries');
        },
        error: (error: HttpErrorResponse) => {
          switch (true) {
            case error.status >= 500 && error.status <= 599:
              this.messageService.add({
                severity: 'error',
                summary: 'Serverfehler',
                detail:
                  'Beim Erstellen des Eintrags ist ein Fehler aufgetreten. Bitte versuche es erneut.',
              });
              break;
            case error.status == 400:
              this.messageService.add({
                severity: 'error',
                summary: 'Clientfehler',
                detail:
                  'Die Anwendung scheint falsche Daten an den Server zu senden.',
              });
              break;
            default:
              break;
          }
        },
      });
  }

  updateEntry() {
    this.api
      .updateWeightEntry(
        this.entryId!,
        this.getWeighedAt(),
        this.formGroup.controls['weight'].value,
        this.formGroup.controls['comment'].value,
        this.formGroup.controls['rabbit'].value!.id,
        this.formGroup.controls['medicines'].value?.map((x) => x.id) ?? [],
      )
      .subscribe({
        next: (_) => {
          this.router.navigateByUrl('/weight-entries');
        },
        error: (error: HttpErrorResponse) => {
          switch (true) {
            case error.status >= 500 && error.status <= 599:
              this.messageService.add({
                severity: 'error',
                summary: 'Serverfehler',
                detail:
                  'Beim Aktualisieren des Eintrags ist ein Fehler aufgetreten. Bitte versuche es erneut.',
              });
              break;
            case error.status == 400:
              this.messageService.add({
                severity: 'error',
                summary: 'Clientfehler',
                detail:
                  'Die Anwendung scheint falsche Daten an den Server zu senden.',
              });
              break;
            default:
              break;
          }
        },
      });
  }

  getWeighedAt(): Date {
    const now: Date = new Date(Date.now());
    const date: Date = this.formGroup.controls['date'].value ?? now;
    const time: Date = this.formGroup.controls['time'].value ?? now;
    const weighedAt: Date = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes(),
      0,
    );

    return weighedAt;
  }
}
